import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import AdminLayout from '@/components/AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertCircle, X, ChevronDown,
  ChevronUp, Download, Package, Eye, Loader2, Image as ImageIcon,
  RefreshCw, ArrowRight, Info, FolderArchive
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';
const API = `${BACKEND_URL}/api`;

// ─── Drag & Drop Zone ──────────────────────────────────────────────────────────
const DropZone = ({ onFile, file, loading }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && dropped.name.match(/\.(xlsx|xlsm)$/i)) {
      onFile(dropped);
    } else {
      toast.error('Please drop an .xlsx Excel file');
    }
  }, [onFile]);

  return (
    <div
      className={`relative border-2 border-dashed rounded-2xl p-10 text-center transition-all duration-200 cursor-pointer
        ${dragging ? 'border-[#0c1b44] bg-[#0c1b44]/5 scale-[1.01]' : 'border-gray-200 hover:border-[#0c1b44]/40 hover:bg-gray-50'}
        ${file ? 'border-emerald-400 bg-emerald-50' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => !loading && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xlsm"
        className="hidden"
        onChange={(e) => e.target.files[0] && onFile(e.target.files[0])}
      />

      {file ? (
        <div className="flex flex-col items-center gap-3">
          <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center">
            <FileSpreadsheet className="h-8 w-8 text-emerald-600" />
          </div>
          <div>
            <p className="font-bold text-emerald-700 text-lg">{file.name}</p>
            <p className="text-sm text-emerald-600">{(file.size / 1024).toFixed(1)} KB — Ready to parse</p>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700 border-0">
            <CheckCircle2 className="h-3 w-3 mr-1" /> File selected
          </Badge>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4">
          <div className="w-20 h-20 bg-gradient-to-br from-[#0c1b44]/10 to-[#d4af37]/10 rounded-2xl flex items-center justify-center">
            <Upload className="h-10 w-10 text-[#0c1b44]/60" />
          </div>
          <div>
            <p className="text-lg font-bold text-[#0c1b44] mb-1">Drop your Excel file here</p>
            <p className="text-sm text-gray-500">or click to browse — supports .xlsx format</p>
          </div>
          <div className="flex gap-2 flex-wrap justify-center">
            {['DOCOL', 'HIKVISION', 'GEZE', 'OSRAM', 'DORMA'].map(b => (
              <span key={b} className="text-xs bg-[#0c1b44]/10 text-[#0c1b44] px-2 py-1 rounded-full font-medium">{b}</span>
            ))}
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">+ more sheets</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Sheet Summary Pills ───────────────────────────────────────────────────────
const SheetSummary = ({ sheets }) => (
  <div className="flex flex-wrap gap-2 mt-4">
    {sheets.map(s => (
      <div key={s.sheet} className="flex items-center gap-2 bg-white border rounded-xl px-3 py-2 shadow-sm">
        <FileSpreadsheet className="h-4 w-4 text-[#0c1b44]" />
        <span className="font-semibold text-sm text-[#0c1b44]">{s.brand}</span>
        <Badge variant="secondary" className="text-xs">{s.product_count} products</Badge>
        {s.images_found > 0 && (
          <Badge className="text-xs bg-emerald-100 text-emerald-700 border-0">
            <ImageIcon className="h-3 w-3 mr-1" />{s.images_found} images
          </Badge>
        )}
        {s.image_sources && Object.entries(s.image_sources).map(([src, count]) => (
          <Badge key={src} className={`text-[10px] border-0 ${src === 'url' ? 'bg-blue-100 text-blue-700' : src === 'zip' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-50 text-emerald-600'}`}>
            {src === 'url' ? '🔗' : src === 'zip' ? '📦' : '📄'} {count}
          </Badge>
        ))}
        {s.images_found === 0 && (
          <Badge className="text-xs bg-gray-100 text-gray-400 border-0">no images</Badge>
        )}
      </div>
    ))}
  </div>
);

// ─── Product Preview Card ──────────────────────────────────────────────────────
const ProductPreviewRow = ({ product, index }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = product._meta || {};

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
      {/* Row summary */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#0c1b44]/5"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Image thumbnail or fallback icon */}
        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 border">
          {product.images?.[0] ? (
            <img
              src={product.images[0]}
              alt={product.name}
              className="w-full h-full object-cover"
              onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
            />
          ) : null}
          <div className={`w-full h-full items-center justify-center ${product.images?.[0] ? 'hidden' : 'flex'}`}>
            <ImageIcon className="h-4 w-4 text-gray-300" />
          </div>
        </div>

        {/* Brand badge */}
        <Badge variant="outline" className="text-[10px] flex-shrink-0">{meta.source_sheet || product.brand}</Badge>

        {/* Name */}
        <p className="flex-1 font-medium text-sm text-navy truncate">{product.name}</p>

        {/* SKU */}
        <code className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600 hidden sm:block">{product.sku}</code>

        {/* Price */}
        <span className="text-sm font-bold text-[#0c1b44] whitespace-nowrap">AED {product.price}</span>

        {/* Stock */}
        <Badge className={`text-[10px] ${product.stock > 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
          Qty: {product.stock}
        </Badge>

        {/* Expand toggle */}
        {expanded ? <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />}
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t bg-white grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          {/* Specifications */}
          {product.specifications && Object.keys(product.specifications).length > 0 && (
            <div>
              <p className="text-xs font-bold text-muted-foreground mb-2 uppercase tracking-wide">Specifications</p>
              <div className="space-y-1">
                {Object.entries(product.specifications).slice(0, 6).map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <span className="text-muted-foreground text-xs w-24 flex-shrink-0">{k}:</span>
                    <span className="text-xs font-medium text-navy truncate">{v}</span>
                  </div>
                ))}
                {Object.keys(product.specifications).length > 6 && (
                  <p className="text-xs text-muted-foreground">+{Object.keys(product.specifications).length - 6} more...</p>
                )}
              </div>
            </div>
          )}

          {/* Highlights */}
          {product.highlights?.length > 0 && (
            <div>
              <p className="text-xs font-bold text-muted-foreground mb-2 uppercase tracking-wide">Highlights</p>
              <ul className="space-y-1">
                {product.highlights.slice(0, 5).map((h, i) => (
                  <li key={i} className="text-xs text-navy flex gap-1">
                    <span className="text-[#d4af37] mt-0.5">•</span> {h}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Meta info */}
          <div>
            <p className="text-xs font-bold text-muted-foreground mb-2 uppercase tracking-wide">Import Info</p>
            <div className="space-y-1">
              <div className="flex gap-2">
                <span className="text-muted-foreground text-xs w-24">GP Code:</span>
                <span className="text-xs font-mono font-medium">{meta.gp_code || '—'}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-muted-foreground text-xs w-24">Supplier Code:</span>
                <span className="text-xs font-mono">{meta.original_code || '—'}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-muted-foreground text-xs w-24">Mark Up:</span>
                <span className="text-xs font-medium">{meta.markup_pct ? `${meta.markup_pct}%` : '—'}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-muted-foreground text-xs w-24">Image:</span>
                <span className={`text-xs font-medium ${meta.has_image ? 'text-emerald-600' : 'text-gray-400'}`}>
                  {meta.has_image
                    ? meta.image_source === 'url' ? '🔗 Downloaded from URL'
                    : meta.image_source === 'zip' ? '📦 Matched from ZIP'
                    : meta.image_source === 'embedded' ? '📄 Extracted from Excel'
                    : '✅ Image found'
                    : '❌ No image found'}
                </span>
              </div>
              {meta.supplier_link && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground text-xs w-24">Supplier Link:</span>
                  <a href={meta.supplier_link} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline truncate max-w-[160px]">
                    {meta.supplier_link.length > 30 ? meta.supplier_link.slice(0, 30) + '...' : meta.supplier_link}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const AdminBulkUpload = () => {
  const { user, getAuthHeaders } = useApp();
  const navigate = useNavigate();

  // State
  const [file, setFile] = useState(null);
  const [imagesZip, setImagesZip] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(''); // 'uploading' or 'processing'
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [preview, setPreview] = useState(null);  // { session_id, total_products, sheets, products }
  const [sessionId, setSessionId] = useState(null); // server-side cache key
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [importResult, setImportResult] = useState(null);
  const [filterSheet, setFilterSheet] = useState('all');

  // Load categories on mount
  React.useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    axios.get(`${API}/categories/all`, { headers: getAuthHeaders() })
      .then(res => setCategories(res.data || []))
      .catch(() => toast.error('Failed to load categories'));
  }, [user, navigate, getAuthHeaders]);

  // Step 1: Parse the Excel (preview only)
  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    setUploadStatus('uploading');
    setUploadProgress(0);
    setPreview(null);
    setImportResult(null);
    setSessionId(null);

    const formData = new FormData();
    formData.append('file', file);
    if (imagesZip) formData.append('images_zip', imagesZip);

    try {
      const res = await axios.post(`${API}/admin/bulk-upload/preview`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 min — images are downloaded during preview
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
            if (percentCompleted >= 100) {
              setUploadStatus('processing');
            }
          }
        }
      });
      setPreview(res.data);
      setSessionId(res.data.session_id); // save cache key for confirm step
      toast.success(`✅ Parsed ${res.data.total_products} products from ${res.data.sheets.length} sheet(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse Excel file');
    }
    setParsing(false);
  };

  // Step 2: Confirm import to database (uses session_id — no re-upload)
  const handleImport = async () => {
    if (!sessionId) {
      toast.error('Please parse the Excel file first (click Parse & Preview)');
      return;
    }

    setImporting(true);
    setImportProgress({ imported: 0, total: preview?.total_products || 0 });
    const finalCategoryId = (!selectedCategory || selectedCategory === 'none') ? '' : selectedCategory;

    const intervalId = setInterval(async () => {
      try {
        const statusRes = await axios.get(`${API}/admin/bulk-upload/status/${sessionId}`, { headers: getAuthHeaders() });
        if (statusRes.data && statusRes.data.total > 0) {
          setImportProgress(statusRes.data);
        }
      } catch(e) {}
    }, 1000);

    try {
      // Send JSON — no file re-upload. Images already downloaded during preview.
      const res = await axios.post(
        `${API}/admin/bulk-upload/confirm`,
        { session_id: sessionId, category_id: finalCategoryId, skip_duplicates: skipDuplicates },
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, timeout: 600000 }
      );
      clearInterval(intervalId);
      setImportProgress(null);
      setImportResult(res.data);
      setSessionId(null);
      if (res.data.imported > 0) {
        toast.success(`🎉 Successfully imported ${res.data.imported} products!`);
      }
      if (res.data.skipped > 0) {
        toast.info(`Skipped ${res.data.skipped} duplicate products`);
      }
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} products had errors`);
      }
    } catch (e) {
      clearInterval(intervalId);
      setImportProgress(null);
      const detail = e.response?.data?.detail || 'Import failed';
      toast.error(detail);
      if (detail.includes('expired') || detail.includes('session')) {
        toast.info('Please click Parse & Preview again to refresh');
        setSessionId(null);
      }
    }
    setImporting(false);
  };

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/admin/bulk-upload/template`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'grand_palace_product_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  // Filter products by sheet
  const visibleProducts = preview?.products?.filter(p =>
    filterSheet === 'all' || (p._meta?.source_sheet || p.brand) === filterSheet
  ) || [];

  return (
    <AdminLayout title="Bulk Upload" subtitle="Import products from Excel spreadsheet">

      {/* ── Top action bar ── */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Button
          variant="outline"
          onClick={handleDownloadTemplate}
          className="gap-2 border-[#0c1b44]/30 text-[#0c1b44] hover:bg-[#0c1b44]/5"
        >
          <Download className="h-4 w-4" />
          Download Excel Template
        </Button>
        <div className="flex-1" />
        {importResult && (
          <Button onClick={() => navigate('/admin/products')} className="bg-[#0c1b44] text-white gap-2">
            <Package className="h-4 w-4" /> View All Products <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* ── Info Banner ── */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex gap-3">
        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-bold mb-1">How it works:</p>
          <ol className="list-decimal ml-4 space-y-0.5">
            <li>Upload your Excel file (use the template for best results)</li>
            <li><strong>Optional:</strong> Upload a ZIP of product images (filenames = supplier code, e.g. <code className="bg-blue-100 px-1 rounded">DC-1234.jpg</code>)</li>
            <li>Preview the parsed products and check data</li>
            <li>Select a category and click Import to database</li>
          </ol>
          <p className="mt-2 font-medium">3 ways to add images (priority order):</p>
          <div className="flex flex-wrap gap-2 mt-1">
            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">🔗 Image URL in column A</span>
            <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">📦 ZIP file (matched by code)</span>
            <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs px-2 py-1 rounded-full">📄 Embedded in Excel</span>
          </div>
        </div>
      </div>

      {/* ── Drop zone ── */}
      <Card className="mb-6 border-0 shadow-sm">
        <CardContent className="p-6">
          <DropZone onFile={setFile} file={file} loading={parsing} />

          {/* ZIP Images Drop Zone */}
          <div className="mt-4">
            <div
              className={`border-2 border-dashed rounded-xl p-5 text-center transition-all cursor-pointer
                ${imagesZip ? 'border-purple-400 bg-purple-50' : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/30'}`}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.zip';
                input.onchange = (e) => e.target.files[0] && setImagesZip(e.target.files[0]);
                input.click();
              }}
            >
              {imagesZip ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <FolderArchive className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-purple-700 text-sm">{imagesZip.name}</p>
                    <p className="text-xs text-purple-500">{(imagesZip.size / 1024 / 1024).toFixed(1)} MB</p>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); setImagesZip(null); }} className="ml-2 text-purple-400 hover:text-purple-600">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-3">
                  <FolderArchive className="h-6 w-6 text-gray-400" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-600">Upload Images ZIP <span className="text-gray-400">(optional)</span></p>
                    <p className="text-xs text-gray-400">Name files by supplier code: DC-1234.jpg, GP-DOC-001.png</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-4 items-start">
            {file && (
              <div className="flex-1 max-w-md w-full">
                <div className="flex gap-3">
                  <Button
                    onClick={handleParse}
                    disabled={parsing}
                    className="bg-[#0c1b44] text-white hover:bg-[#1a2d64] gap-2 flex-1 sm:flex-none"
                  >
                    {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                    {parsing 
                      ? (uploadStatus === 'uploading' ? `Uploading... ${uploadProgress}%` : 'Processing & Downloading...') 
                      : 'Parse & Preview'}
                  </Button>
                  {!parsing && (
                    <Button
                      variant="outline"
                      onClick={() => { setFile(null); setImagesZip(null); setPreview(null); setImportResult(null); }}
                      className="gap-2"
                    >
                      <X className="h-4 w-4" /> Clear
                    </Button>
                  )}
                </div>
                
                {parsing && (
                  <div className="mt-3 w-full">
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-300 ${uploadStatus === 'processing' ? 'bg-[#d4af37] w-full animate-pulse' : 'bg-[#0c1b44]'}`}
                        style={{ width: uploadStatus === 'processing' ? '100%' : `${uploadProgress}%` }}
                      ></div>
                    </div>
                    {uploadStatus === 'processing' && (
                      <p className="text-xs text-muted-foreground mt-1.5 animate-pulse flex items-center gap-1">
                        <Info className="h-3 w-3" /> Please wait while the server parses rows and downloads images.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Import Result ── */}
      {importResult && (
        <Card className={`mb-6 border-2 ${importResult.errors?.length > 0 ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle2 className={`h-6 w-6 ${importResult.errors?.length > 0 ? 'text-amber-600' : 'text-emerald-600'}`} />
              <h3 className="font-bold text-lg text-navy">Import Complete</h3>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-2xl font-black text-emerald-600">{importResult.imported}</p>
                <p className="text-xs text-muted-foreground">Imported</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-2xl font-black text-amber-600">{importResult.skipped}</p>
                <p className="text-xs text-muted-foreground">Skipped (duplicates)</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-2xl font-black text-red-500">{importResult.errors?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Errors</p>
              </div>
            </div>
            {importResult.errors?.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-bold text-amber-700 mb-1">Errors:</p>
                {importResult.errors.slice(0, 5).map((err, i) => (
                  <p key={i} className="text-xs text-red-600">• {err.sku}: {err.error}</p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Preview Panel ── */}
      {preview && (
        <div>
          {/* Sheet summary */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-navy">
                Preview — {preview.total_products} Products Found
              </h2>
              <SheetSummary sheets={preview.sheets} />
            </div>
          </div>

          {/* Import controls */}
          <Card className="mb-5 border-[#0c1b44]/20">
            <CardContent className="p-5">
              <h3 className="font-bold text-navy mb-4 flex items-center gap-2">
                <Package className="h-5 w-5 text-[#d4af37]" /> Import Settings
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-semibold text-navy">Fallback Category (Optional)</Label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select a fallback category..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (Use Excel categories)</SelectItem>
                      {categories.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Optional: Categories defined in the Excel file will be used automatically. If no category is found in Excel, this will be used as the default.</p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="font-semibold text-navy text-sm">Skip Duplicate SKUs</Label>
                      <p className="text-xs text-muted-foreground">Products already in DB won't be re-imported</p>
                    </div>
                    <Switch checked={skipDuplicates} onCheckedChange={setSkipDuplicates} />
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t flex flex-col sm:flex-row gap-3 items-start">
                <div className="flex-1 w-full max-w-md">
                  <div className="flex gap-3">
                    <Button
                      onClick={handleImport}
                      disabled={importing || !sessionId}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 h-11 px-6 flex-1 sm:flex-none"
                    >
                      {importing ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Importing...</>
                      ) : (
                        <><Upload className="h-4 w-4" /> Import {preview.total_products} Products to Database</>
                      )}
                    </Button>
                    <Button variant="outline" className="h-11" onClick={() => { setFile(null); setImagesZip(null); setPreview(null); setImportResult(null); }} disabled={importing}>
                      <RefreshCw className="h-4 w-4 mr-2" /> Start Over
                    </Button>
                  </div>

                  {importing && importProgress && importProgress.total > 0 && (
                    <div className="mt-3 w-full animate-in fade-in slide-in-from-top-2">
                      <div className="flex justify-between text-xs text-emerald-700 font-semibold mb-1">
                        <span>Uploading one by one to DB...</span>
                        <span>{importProgress.imported} / {importProgress.total}</span>
                      </div>
                      <div className="w-full bg-emerald-100 rounded-full h-2.5 overflow-hidden">
                        <div 
                          className="bg-emerald-600 h-full rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${Math.round((importProgress.imported / importProgress.total) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sheet filter */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm font-medium text-muted-foreground">Filter by brand:</span>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setFilterSheet('all')}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors
                  ${filterSheet === 'all' ? 'bg-[#0c1b44] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                All ({preview.total_products})
              </button>
              {preview.sheets.map(s => (
                <button
                  key={s.sheet}
                  onClick={() => setFilterSheet(s.brand)}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors
                    ${filterSheet === s.brand ? 'bg-[#0c1b44] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {s.brand} ({s.product_count})
                </button>
              ))}
            </div>
          </div>

          {/* Product list */}
          <div className="space-y-2">
            {visibleProducts.map((product, i) => (
              <ProductPreviewRow key={`${product.sku}-${i}`} product={product} index={i} />
            ))}
          </div>

          {visibleProducts.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <AlertCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>No products for this filter</p>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminBulkUpload;
